import React from 'react';
import classNames from 'classnames';
import isEqual from 'lodash/isEqual';
import ReactSelect, {
  Props,
  SingleValueProps,
  DropdownIndicatorProps,
  OptionProps,
  MenuProps,
  ControlProps,
  SelectComponentsConfig,
  MenuPlacement,
  ActionMeta,
  OnChangeValue,
  components,
} from 'react-select';
import EllipsisWithTooltip from '../ellipsis-with-tooltip';
import { colors } from '../../style';
import MenuItem from '../menu-popover/menu-item';
import Icon from '../icon';
import { PopoverContentContainer } from '../popover';
import StyledSelect, { SELECT_OPTIONS_MAX_HEIGHT } from './styled-select';

const CustomDropdownIndicator = (props: DropdownIndicatorProps<SelectOption>) => {
  return (
    <components.DropdownIndicator {...props}>
      <Icon name={'CHEVRON_DOWN'} variant={props.isDisabled ? 'gray600' : 'gray900'} size={'lg'} />
    </components.DropdownIndicator>
  );
};

const CustomOption = (props: OptionProps<SelectOption>) => {
  return (
    <components.Option {...props}>
      <div>
        <MenuItem
          data-name={'option'}
          disabled={props.isDisabled}
          text={props.label}
          active={props.isSelected}
          marked={props.isFocused}
        />
      </div>
    </components.Option>
  );
};

const ValueContainer = ({ children, className }: { children: React.ReactNode; className?: string }) => {
  return <div className={classNames('value-container', className)}>{children}</div>;
};

const SingleValue = ({ children, ...rest }: SingleValueProps<SelectOption>) => (
  <components.SingleValue {...rest}>
    <EllipsisWithTooltip>{children}</EllipsisWithTooltip>
  </components.SingleValue>
);

const { Menu } = components;

interface OptionBase {
  [key: string]: any;

  label?: string;
  value?: any;
  disabled?: boolean;
}

export type SelectOption = OptionBase;

export interface DeprecatedSelectProps extends Omit<Props<SelectOption, false, any>, 'isLoading' | 'value'> {
  value?: any;
  id?: string;
  isFetching?: boolean;
  allowClear?: boolean;
  disabled?: boolean;
  allowClearText?: string;
  stopPropagation?: boolean;
  preventDefault?: boolean;
  className?: string;
  menuPlacement?: MenuPlacement;
  'data-name'?: string;
  'data-testid'?: string;
  active?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-invalid'?: Props['aria-invalid'];
  shouldAttachPortal?: boolean;
  isSearchable?: boolean;
  ref?: any;
  allowClearValue?: any;
  noOptionsMessage?: () => string;
}

const defaultGetOptionValue = (option: SelectOption) => option.value;
const SelectComponent = ({
  id,
  className,
  disabled,
  placeholder,
  value,
  isFetching,
  allowClear,
  allowClearText = 'None',
  menuPlacement,
  options,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  'aria-invalid': ariaInvalid,
  'data-name': dataName,
  'data-testid': dataTestId,
  getOptionLabel = (option: SelectOption) => option.label || '',
  onChange,
  stopPropagation,
  preventDefault,
  shouldAttachPortal,
  getOptionValue = defaultGetOptionValue,
  active,
  isOptionDisabled,
  isSearchable: isSearchableProp = true,
  ref,
  required,
  onBlur,
  allowClearValue,
  defaultMenuIsOpen,
  noOptionsMessage,
}: DeprecatedSelectProps) => {
  const selectedOption =
    options?.find?.(option => isEqual(getOptionValue(option), value) || option.label === value) || null;

  const renderedOptions =
    allowClear && !!options?.length
      ? [
          {
            label: allowClearText,
            value: allowClearValue ?? null,
          },
          ...options,
        ]
      : options || [];

  // this state is used only to keep track if the menu is open or closed
  // in order to scroll it into view when menu is opened, it is not and should not be passed to the select
  // component itself
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const menuDropdownRef = React.useRef<HTMLDivElement | null>(null);

  const onMenuOpen = () => setIsMenuOpen(true);

  const onMenuClose = () => setIsMenuOpen(false);

  React.useEffect(() => {
    if (isMenuOpen) {
      const selectDropdown = menuDropdownRef.current;
      if (selectDropdown) {
        selectDropdown?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
    }
  }, [isMenuOpen]);

  const components = React.useMemo<SelectComponentsConfig<SelectOption, false, any>>(
    () => ({
      Option: CustomOption,
      Control: ({ innerProps, innerRef, children, isDisabled, ...rest }: ControlProps<SelectOption>) => {
        return (
          <StyledSelect.TogglingContainer
            className={'select-control'}
            disabled={isDisabled}
            ref={innerRef}
            {...innerProps}
            active={rest.isFocused || active}
            data-testid={'select-control'}
          >
            {children}
          </StyledSelect.TogglingContainer>
        );
      },
      ValueContainer,
      SingleValue,
      DropdownIndicator: isFetching ? null : CustomDropdownIndicator,
      Menu: ({ children, className, ...rest }: MenuProps<SelectOption>) => (
        <Menu className={className + ' ' + 'select-dropdown'} {...rest}>
          <div ref={menuDropdownRef} data-testid={'select-dropdown'} role={'listbox'}>
            <PopoverContentContainer overflowX={'hidden'} padding={'xs'} size={'auto'}>
              {children}
            </PopoverContentContainer>
          </div>
        </Menu>
      ),
      IndicatorSeparator: () => null,
    }),

    [active, isFetching],
  );

  const onOptionSelected = (selectedOption: OnChangeValue<OptionBase, false>, actionMeta: ActionMeta<OptionBase>) => {
    if (actionMeta.option?.disabled) {
      return;
    }

    onChange?.(selectedOption, actionMeta);
  };
  const onSelectClick = (e: React.KeyboardEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>) => {
    if (preventDefault) {
      e.preventDefault();
    }
    if (stopPropagation) {
      e.stopPropagation();
    }
  };

  // note: select doesnt work inside radix modal when is rendered into portal
  const portalProps = React.useMemo(() => {
    return shouldAttachPortal
      ? {
          menuPortalTarget: document.getElementById('portal'),
          closeMenuOnScroll: true,
        }
      : {};
  }, [shouldAttachPortal]);

  // allow search only if renderItems resolves to string, if renderItem returns something else, react-select dont know how to support it.
  const isSearchable = isSearchableProp && renderedOptions.every(item => typeof getOptionLabel(item) === 'string');

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      onKeyDown={onSelectClick}
      onClick={onSelectClick}
      data-value={selectedOption?.value}
      className={classNames('box-select', className, { disabled })}
      data-name={dataName}
      data-testid={dataTestId || 'select-comp'}
      ref={ref}
      style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
    >
      <ReactSelect
        onMenuClose={onMenuClose}
        onMenuOpen={onMenuOpen}
        aria-label={ariaLabel || (typeof placeholder === 'string' ? placeholder : undefined)}
        isOptionDisabled={isOptionDisabled || (option => !!option.disabled)}
        aria-labelledby={ariaLabelledby}
        aria-invalid={ariaInvalid}
        styles={{
          option: () => ({}),
          singleValue: (base: any) => ({ ...base, maxWidth: '80%' }),
          placeholder: (base: any) => ({ ...base, color: colors.gray600 }),
          menu: (base: any) => ({
            ...base,
            boxShadow: 'none',
            background: 'transparent',
            marginTop: 0,
            zIndex: 2000,
          }),
          menuList: (base: any) => ({ ...base, overflowX: 'hidden' }),
          menuPortal: (base: any) => ({ ...base, zIndex: 10000 }),
          dropdownIndicator: (base: any) => ({
            ...base,
            flexGrow: 1,
            position: 'absolute',
            right: 0,
            top: 3,
          }),
        }}
        placeholder={placeholder || (allowClear && !selectedOption && allowClearText)}
        menuPlacement={menuPlacement}
        noOptionsMessage={noOptionsMessage}
        components={components}
        blurInputOnSelect
        options={renderedOptions}
        isLoading={isFetching}
        onChange={onOptionSelected}
        required={required}
        isDisabled={disabled}
        value={selectedOption}
        isSearchable={isSearchable}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        maxMenuHeight={SELECT_OPTIONS_MAX_HEIGHT}
        menuShouldScrollIntoView
        defaultMenuIsOpen={defaultMenuIsOpen}
        {...portalProps}
        inputId={id}
        onBlur={onBlur}
      />
    </div>
  );
};

/**
 * @deprecated This component is deprecated. Please use Select instead.
 */

const DeprecatedSelect = (props: DeprecatedSelectProps) => <SelectComponent {...props} />;

export default DeprecatedSelect;
